import { range, streamRng } from '../math/rng.js';
import { STATE } from './ai.js';

/**
 * @file The ring spawner: keeps a live population AROUND the player without the
 * player ever seeing one appear.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE THREE RULES
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. **Ring, not area.** Candidates land between `ringMin` and `ringMax` metres
 *    (25–45 by default). Closer and they materialise in the player's lap;
 *    further and they spend their whole life walking in from off screen.
 * 2. **Never inside the view cone.** The camera looks along `-(sin yaw, cos yaw)`
 *    from behind the player, so a spawn in THAT direction is on screen. A
 *    candidate whose bearing from the player falls inside the forward cone
 *    (`coneDeg` wide, i.e. +-33 degrees by default) is rejected outright. This is
 *    the whole reason the spawner needs `camYaw`.
 * 3. **Never inside geometry.** `collision.blocked(x, z, r)` rejects spawns in
 *    walls; without it enemies pop out of buildings and then get shoved out by
 *    the resolver, which looks like teleporting.
 *
 * Culling is the mirror image: anything past `despawn` metres (70) is removed
 * SILENTLY — `manager.despawn()`, not `kill()`, because a death animation for
 * something 70 m behind you is a lie about what happened.
 *
 * No three.js here: the spawner is pure maths over the manager's arrays, so the
 * unit tests drive it with a plain `{x,y,z}` player.
 *
 * @module engine/actors/Spawner
 */

/** @typedef {import('./EnemyManager.js').EnemyManager} EnemyManager */
/** @typedef {import('../types.js').Rng} Rng */

/**
 * @typedef {Object} SpawnRule
 * @property {number} typeIdx  Index into `manager.types`.
 * @property {number} [weight=1] Relative pick weight among rules that still
 *   have budget.
 * @property {number} maxAlive Population cap FOR THIS TYPE.
 * @property {number} [ringMin=25] Metres.
 * @property {number} [ringMax=45] Metres.
 * @property {number} [radius] Clearance test radius; defaults to the type's.
 */

const TAU = Math.PI * 2;

export class Spawner {
  /**
   * @param {Object} o
   * @param {EnemyManager} o.manager
   * @param {SpawnRule[]} o.rules
   * @param {Rng|string|number} [o.rng] An `Rng`, or a seed to derive one from.
   * @param {{blocked?: Function}|null} [o.collision] Clearance source.
   * @param {import('../types.js').Bounds2|null} [o.bounds] Spawns are clamped
   *   OUT of existence (rejected) outside this, so a small level does not grow
   *   enemies in the void.
   * @param {(x: number, z: number) => number} [o.heightAt]
   * @param {number} [o.despawn=70] Metres. Must exceed `ringMax` by a healthy
   *   margin or enemies thrash in and out at the boundary.
   * @param {number} [o.coneDeg=66] FULL width of the forbidden camera-forward
   *   cone, degrees. 66 => +-33 degrees, which covers a 30-degree-FOV camera
   *   plus slack for the look-ahead.
   * @param {number} [o.rate=10] Spawn ATTEMPTS per second while under budget.
   *   High on purpose: the ring has to refill behind a player running at
   *   6.5 m/s.
   * @param {number} [o.tries=6] Candidate positions per attempt before giving
   *   up for this attempt (dense levels reject a lot).
   * @param {number} [o.warmup=0] Enemies to place instantly on the first
   *   `update` (skips the fade-in; the cone rule still applies).
   */
  constructor(o) {
    const {
      manager,
      rules = [],
      rng = 'spawner',
      collision = null,
      bounds = null,
      heightAt = null,
      despawn = 70,
      coneDeg = 66,
      rate = 10,
      tries = 6,
      warmup = 0,
    } = o ?? {};

    if (!manager) throw new Error('Spawner: `manager` is required');

    /** @type {EnemyManager} */
    this.manager = manager;
    /** @type {SpawnRule[]} */
    this.rules = rules.map((r) => ({
      weight: 1,
      ringMin: 25,
      ringMax: 45,
      ...r,
    }));
    /** @type {Rng} */
    this.rng = typeof rng === 'function' ? rng : streamRng(rng, 'spawner');
    this.collision = collision;
    this.bounds = bounds;
    this.heightAt = typeof heightAt === 'function' ? heightAt : null;
    this.despawn = despawn;
    this.despawn2 = despawn * despawn;
    /** Cosine of the HALF cone — the rejection test is `dot > cosHalf`. */
    this.cosHalf = Math.cos((coneDeg * Math.PI) / 180 / 2);
    this.rate = rate;
    this.tries = Math.max(1, tries | 0);
    this.warmup = warmup | 0;

    /** @type {number} Fractional spawn attempts carried between steps. */
    this._acc = 0;
    /** @type {number} Total spawned, for the HUD. */
    this.spawned = 0;
    /** @type {number} Total distance-culled. */
    this.culled = 0;
    /** @type {number} Candidates rejected (cone / blocked / bounds). */
    this.rejected = 0;
    this._warmed = false;
  }

  /**
   * One fixed step: cull what wandered off, then top the population back up.
   *
   * @param {number} dt Seconds.
   * @param {{x: number, y?: number, z: number}} playerPos
   * @param {number} [camYaw=0] Live rig yaw, radians.
   */
  update(dt, playerPos, camYaw = 0) {
    if (!playerPos) return;
    const m = this.manager;
    const px = playerPos.x;
    const pz = playerPos.z;

    // ── 1. distance cull ─────────────────────────────────────────────────────
    for (let i = 0; i < m.top; i++) {
      if (m.state[i] === STATE.DEAD) continue;
      const dx = m.x[i] - px;
      const dz = m.z[i] - pz;
      if (dx * dx + dz * dz > this.despawn2) {
        m.despawn(i);
        this.culled++;
      }
    }

    // ── 2. budget ────────────────────────────────────────────────────────────
    let attempts;
    if (!this._warmed && this.warmup > 0) {
      this._warmed = true;
      attempts = this.warmup * this.tries;
    } else {
      this._acc += dt * this.rate;
      attempts = Math.floor(this._acc);
      this._acc -= attempts;
    }
    if (attempts <= 0) return;

    // Camera FORWARD in world XZ (see the header): the camera sits at
    // `target + (sin yaw, _, cos yaw) * d` and looks back at the target.
    const fx = -Math.sin(camYaw);
    const fz = -Math.cos(camYaw);

    for (let a = 0; a < attempts; a++) {
      const rule = this._pickRule();
      if (!rule) break;
      if (!this._trySpawn(rule, px, pz, fx, fz)) this.rejected++;
    }
  }

  /** Resets the counters (a new wave, a respawn). */
  reset() {
    this._acc = 0;
    this._warmed = false;
    this.spawned = 0;
    this.culled = 0;
    this.rejected = 0;
  }

  /** @returns {{spawned: number, culled: number, rejected: number, alive: number}} */
  stats() {
    return {
      spawned: this.spawned,
      culled: this.culled,
      rejected: this.rejected,
      alive: this.manager.alive,
    };
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /**
   * Weighted pick among the rules that are still UNDER their `maxAlive`. Rules
   * at budget are skipped entirely rather than picked-and-rejected, so a full
   * type never starves a hungry one.
   * @returns {SpawnRule|null}
   */
  _pickRule() {
    const m = this.manager;
    let total = 0;
    for (const r of this.rules) {
      if (m.aliveByType[r.typeIdx & 255] < r.maxAlive) total += r.weight;
    }
    if (total <= 0) return null;
    let pick = this.rng() * total;
    for (const r of this.rules) {
      if (m.aliveByType[r.typeIdx & 255] >= r.maxAlive) continue;
      pick -= r.weight;
      if (pick <= 0) return r;
    }
    return null;
  }

  /**
   * @param {SpawnRule} rule
   * @param {number} px @param {number} pz
   * @param {number} fx @param {number} fz Camera forward (unit, XZ).
   * @returns {boolean} True when an enemy was placed.
   */
  _trySpawn(rule, px, pz, fx, fz) {
    const m = this.manager;
    const type = m.types[rule.typeIdx];
    const r = rule.radius ?? type?.radius ?? 0.5;

    for (let k = 0; k < this.tries; k++) {
      const a = this.rng() * TAU;
      const dx = Math.sin(a);
      const dz = Math.cos(a);

      // Rule 2: reject the camera-forward wedge BEFORE the expensive tests.
      if (dx * fx + dz * fz > this.cosHalf) continue;

      const dist = range(this.rng, rule.ringMin, rule.ringMax);
      const x = px + dx * dist;
      const z = pz + dz * dist;

      const b = this.bounds;
      if (b && (x < b.minX + r || x > b.maxX - r || z < b.minZ + r || z > b.maxZ - r)) continue;
      if (this.collision?.blocked?.(x, z, r)) continue;

      const y = this.heightAt ? this.heightAt(x, z) : 0;
      const id = m.spawn(rule.typeIdx, x, z, y);
      if (id < 0) return false; // manager full: no candidate will help
      this.spawned++;
      return true;
    }
    return false;
  }
}
